//jsxhook
import {
  Column,
  FormError,
  H6,
  Label2,
  Modal,
  PrimaryButton,
  StandardXAxisPadding,
  TextInput
} from "@sixty-six-north/ui-system"

import { Cart, DiscountCodeInfo, Order } from "@commercetools/platform-sdk"
import { ThemeUIStyleObject } from "@theme-ui/css"
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik"
import React, { useState, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Box, Flex, Grid } from "theme-ui"
import { CartCalculator } from "../../cart/CartCalculator"
import { useCartService, useCartState } from "../../cart/CartServiceHook"
import { Stores } from "../../cart/Stores"
import { useApplePay } from "../../FeatureFlags"
import { useStoreContext } from "../../i18n/StoreHooks"
import { ExpressCheckout } from "../ExpressCheckout"
import { invalidDiscountCodeStates } from "../review/validators/DiscountCodeValidation"
import { AcceptedCreditAndDebitCards } from "./AcceptedCreditAndDebitCards"
import { CheckoutButton } from "./Buttons"
import { CartSummaryLineItem } from "./CartSummaryLineItem"
import { PromoCodeItemsAlert } from "./PromoCodeItemsAlert"
import SummationItem, { SummationAlignment } from "./SummationItem"

export const shouldShowTaxes = (orderOrCart?: Order | Cart) => {
  return (
    orderOrCart?.store?.key === Stores.US.name ||
    orderOrCart?.taxedPrice?.taxPortions?.length === 0
  )
}

export const CartPriceSummary: React.FC = () => {
  return (
    <CartSummaryGrid>
      <CartSubtotal />
      <CartDiscount />
      <CartShipping />
      <CartTaxesAndDuties />
      <CartTotal />
    </CartSummaryGrid>
  )
}

export const OrderPriceSummary: React.FC<{ order?: Order | Cart }> = ({
  order
}) => {
  return (
    <CartSummaryGrid>
      <OrderSubtotal order={order} />
      <OrderDiscount order={order} />
      <OrderShipping order={order} />
      <OrderTaxesAndDuties order={order} />
      <OrderTotal order={order} />
    </CartSummaryGrid>
  )
}

export const CartPageSummary: React.FC<{
  submit: () => Promise<unknown>
  applePaySubmit: () => void
}> = ({ submit, applePaySubmit }) => {
  const applePay = useApplePay()
  const cart = useCartState()

  // const applePay = useApplePay()
  return (
    <CartSummaryContainer>
      <CartSummaryHeader />
      <CartPriceSummary />
      <CurrentPromoCodes
        allowPromoCodesToBeAdded={true}
        includePromoCodeAlert={false}
      />
      <CheckoutButton submit={submit} />
      {applePay && <ExpressCheckout cart={cart} />}
      <HorizontalDivider />
      <AcceptedCreditAndDebitCards />
    </CartSummaryContainer>
  )
}

export const InformationPageSummary: React.FC = ({}) => {
  return (
    <CartSummaryContainer>
      <CartSummaryHeader />
      <CartSummaryLineItems />
      <CartPriceSummary />
      <CurrentPromoCodes allowPromoCodesToBeAdded={true} />
    </CartSummaryContainer>
  )
}
export const PaymentPageSummary: React.FC = ({}) => {
  return (
    <CartSummaryContainer>
      <CartSummaryHeader />
      <CartSummaryLineItems />
      <CartPriceSummary />
      <CurrentPromoCodes allowPromoCodesToBeAdded={true} />
    </CartSummaryContainer>
  )
}

export const ReviewPageSummary: React.FC = ({}) => {
  return (
    <CartSummaryContainer>
      <CartSummaryHeader />
      <CartSummaryLineItems />
      <CartPriceSummary />
      <CurrentPromoCodes allowPromoCodesToBeAdded={true} />
    </CartSummaryContainer>
  )
}

export const ConfirmationPageSummary: React.FC<{ order?: Order | Cart }> = ({
  order
}) => {
  return (
    <CartSummaryContainer>
      <CartSummaryHeader />
      <OrderSummaryLineItems order={order} />
      <OrderPriceSummary order={order} />
      <CurrentPromoCodes order={order} />
    </CartSummaryContainer>
  )
}

export const CartSummaryHeader: React.FC = ({}) => {
  const { t } = useTranslation("checkout")

  return (
    <Box sx={{ mb: 16 }}>
      <H6 sx={{ color: "black.1" }}>
        {t("summary", { defaultValue: "Summary" })}
      </H6>
    </Box>
  )
}

export const CartSubtotal: React.FC = ({}) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  const cart = useCartState()
  return (
    <SummationItem
      id={"cartSubtotal"}
      heading={t("subtotal")}
      value={formatCurrency(CartCalculator.subTotalOriginalPrice(cart))}
      alignment={SummationAlignment.right}
    />
  )
}

export const CartDiscount: React.FC = ({}) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  const cart = useCartState()
  const totalDiscount = CartCalculator.totalDiscount(cart)

  if (totalDiscount.centAmount > 0) {
    return (
      <SummationItem
        id="cartDiscount"
        heading={t("discount")}
        value={formatCurrency(CartCalculator.totalDiscount(cart))}
        alignment={SummationAlignment.right}
      />
    )
  } else {
    return (
      <SummationItem
        id="cartDiscount"
        heading={t("discount")}
        value={"—"}
        alignment={SummationAlignment.right}
      />
    )
  }
}

export const CartShipping: React.FC = ({}) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  const cart = useCartState()
  return (
    <SummationItem
      id="cartShipping"
      heading={t("shipping")}
      value={formatCurrency(CartCalculator.shippingCostCentAmount(cart))}
      alignment={SummationAlignment.right}
    />
  )
}

export const CartTaxesAndDuties: React.FC = () => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  const cart = useCartState()
  const showTaxes = shouldShowTaxes(cart)

  if (showTaxes) {
    return (
      <SummationItem
        id="cartTaxesAndDuties"
        heading={t("taxesAndDuties")}
        value={
          formatCurrency(CartCalculator.taxesCentAmount(cart)) ||
          t("calculatedAtTheNextStep")
        }
        alignment={SummationAlignment.right}
      />
    )
  } else {
    return <React.Fragment />
  }

  // if (taxesAreIncluded) {
  //   return <React.Fragment />
  // } else if (step < CheckoutSteps.Shipping && taxes.centAmount === 0) {
  //   return (
  //     <SummationItem
  //       id="cartTaxesAndDuties"
  //       heading={t("taxesAndDuties")}
  //       value={t("calculatedAtTheNextStep")}
  //       alignment={SummationAlignment.right}
  //     />
  //   )
  // } else {
  //   return (
  //     <SummationItem
  //       id="cartTaxesAndDuties"
  //       heading={t("taxesAndDuties")}
  //       value={formatCurrency(CartCalculator.taxesCentAmount(cart))}
  //       alignment={SummationAlignment.right}
  //     />
  //   )
  // }
}

export const CartTotal: React.FC = ({}) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  const cart = useCartState()
  return (
    <SummationItem
      id="cartTotal"
      heading={t("total")}
      value={formatCurrency(CartCalculator.totalPriceCentAmount(cart))}
      alignment={SummationAlignment.right}
    />
  )
}

export const OrderSubtotal: React.FC<{ order?: Order | Cart }> = ({
  order
}) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  return (
    <SummationItem
      heading={t("subtotal")}
      value={formatCurrency(CartCalculator.subTotalOriginalPrice(order))}
      alignment={SummationAlignment.right}
    />
  )
}

export const OrderDiscount: React.FC<{ order?: Order | Cart }> = ({
  order
}) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  return (
    <SummationItem
      heading={t("discount")}
      value={formatCurrency(CartCalculator.totalDiscount(order))}
      alignment={SummationAlignment.right}
    />
  )
}

export const OrderShipping: React.FC<{ order?: Order | Cart }> = ({
  order
}) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  return (
    <SummationItem
      heading={t("shipping")}
      value={formatCurrency(CartCalculator.shippingCostCentAmount(order))}
      alignment={SummationAlignment.right}
    />
  )
}

export const OrderTotal: React.FC<{ order?: Order | Cart }> = ({ order }) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  return (
    <SummationItem
      heading={t("total")}
      value={formatCurrency(CartCalculator.totalPriceCentAmount(order))}
      alignment={SummationAlignment.right}
    />
  )
}

export const OrderTaxesAndDuties: React.FC<{ order?: Order | Cart }> = ({
  order
}) => {
  const { t } = useTranslation("checkout")
  const { formatCurrency } = useStoreContext()
  const showTaxes = shouldShowTaxes(order)

  if (showTaxes) {
    return (
      <SummationItem
        heading={t("taxesAndDuties")}
        value={formatCurrency(CartCalculator.taxesCentAmount(order))}
        alignment={SummationAlignment.right}
      />
    )
  } else {
    return <React.Fragment />
  }
}

export const CartSummaryContainer: React.FC<{
  children?: ReactNode
  sx?: ThemeUIStyleObject
}> = ({ sx = {}, children }) => {
  return (
    <Box
      sx={{
        bg: "grey.4",
        height: "100%",
        minHeight: [0, null, null, "80vh"],
        display: "flex",
        flexDirection: "column",
        alignItems: ["center", null, null, "flex-start"],
        ...sx
      }}
    >
      <Box
        sx={{
          pl: [24, 24, 48],
          pr: StandardXAxisPadding,
          paddingTop: 64,
          paddingBottom: 64,
          position: "relative",
          width: "100%"
        }}
      >
        <Column>{children}</Column>
      </Box>
    </Box>
  )
}

export const CartSummaryGrid: React.FC<{ children?: ReactNode }> = ({
  children
}) => {
  return (
    <Box sx={{ width: "100%", marginBottom: 24 }}>
      <Grid columns={"2fr 4fr"} sx={{ width: "100%" }}>
        {children}
      </Grid>
    </Box>
  )
}

export const HorizontalDivider: React.FC = ({}) => {
  return (
    <Box sx={{ mb: 16 }} className="divider">
      <Box sx={{ width: "100%", height: 1, bg: "grey.3" }} />
    </Box>
  )
}

export const CartSummaryLineItems: React.FC = ({}) => {
  const cart = useCartState()
  return (
    <Column sx={{ width: "100%" }}>
      {cart &&
        cart.lineItems.map(item => (
          <CartSummaryLineItem item={item} key={item.id} />
        ))}
    </Column>
  )
}

export const OrderSummaryLineItems: React.FC<{ order?: Order | Cart }> = ({
  order
}) => {
  return (
    <Column sx={{ width: "100%" }}>
      {order &&
        order.lineItems.map(item => (
          <CartSummaryLineItem item={item} key={item.id} />
        ))}
    </Column>
  )
}

const AddPromoCode = () => {
  const cartService = useCartService()
  const { t } = useTranslation("checkout")
  const [showModal, setShowModal] = useState(false)

  interface Values {
    promoCode: string
  }

  const onSubmit = (
    values: Values,
    helpers: FormikHelpers<Values>
  ): void | Promise<unknown> => {
    helpers.setSubmitting(true)

    cartService
      .addDiscountCode(values.promoCode)
      .then(() => setShowModal(false))
      .catch(() => helpers.setErrors({ promoCode: t("invalidPromoCode") }))
      .finally(() => helpers.setSubmitting(false))
  }

  return (
    <Box sx={{ pb: 32 }}>
      <Label2 sx={{ a: { color: "blue.0" } }}>
        <a href="#" onClick={() => setShowModal(true)}>
          {t("addPromotionalCode", { defaultValue: "Add a promotional code" })}
        </a>
      </Label2>
      <Modal
        title={t("addPromotionalCode")}
        show={showModal}
        close={() => setShowModal(false)}
      >
        <Formik initialValues={{ promoCode: "" }} onSubmit={onSubmit}>
          {formik => {
            return (
              <Form>
                <Flex
                  sx={{
                    flexDirection: "column"
                  }}
                >
                  <Box sx={{ mb: 8, flexGrow: 1 }}>
                    <Field
                      as={TextInput}
                      autoFocus
                      name="promoCode"
                      sx={{ mb: 8 }}
                    >
                      {t("addPromoCode", { defaultValue: "Add promo code" })}
                    </Field>
                    <ErrorMessage name="promoCode" component={FormError} />
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <PrimaryButton
                      type="submit"
                      disabled={formik.isSubmitting}
                      sx={{ width: "100%" }}
                    >
                      {t("add", { defaultValue: "Add" })}
                    </PrimaryButton>
                  </Box>
                </Flex>
              </Form>
            )
          }}
        </Formik>
      </Modal>
    </Box>
  )
}

export const CurrentPromoCodes: React.FC<{
  order?: Order | Cart
  includePromoCodeAlert?: boolean
  allowPromoCodesToBeAdded?: boolean
}> = ({
  order,
  allowPromoCodesToBeAdded = false,
  includePromoCodeAlert = true
}) => {
  const cart = useCartState()
  const cartService = useCartService()
  const discounts = order?.discountCodes || cart?.discountCodes || []
  const { t } = useTranslation("checkout")

  const removeDiscount = (info: DiscountCodeInfo) => {
    cartService.removeDiscountCode(info.discountCode)
  }

  if (discounts.length === 0 && allowPromoCodesToBeAdded) {
    return <AddPromoCode />
  } else {
    return discounts.length > 0 ? (
      <>
        <Column sx={{ mb: 16 }}>
          <Box sx={{ mb: 4 }}>
            <H6 sx={{ color: "black.1" }}>
              {t("promotionalCode", { defaultValue: "Promotional Code" })}
            </H6>
          </Box>

          {discounts.map(it => {
            return (
              <Flex key={it.discountCode.id}>
                <Box>
                  <Label2 sx={{ color: "grey.1" }}>
                    {it.discountCode.obj?.code}
                  </Label2>
                  {invalidDiscountCodeStates.includes(it.state) && (
                    <Label2 sx={{ color: "red.1", ml: 4 }}>
                      {t("discountCodeWarning", {
                        defaultValue: "Promotional code is no longer valid"
                      })}
                    </Label2>
                  )}
                </Box>
                {!order && (
                  <Box sx={{ marginLeft: "auto" }}>
                    <Label2 sx={{ a: { color: "blue.0" } }}>
                      <a href="#" onClick={() => removeDiscount(it)}>
                        {t("removeDiscount", { defaultValue: "Remove" })}
                      </a>
                    </Label2>
                  </Box>
                )}
              </Flex>
            )
          })}
        </Column>
        {includePromoCodeAlert && <PromoCodeItemsAlert theme="light" />}
      </>
    ) : (
      <Column></Column>
    )
  }
}
